/**
 * SD-2674: inline chart blocks emitted by Allo.
 *
 * The backend embeds a chart in the assistant's markdown as
 * `<chart>{...}</chart>`, buffered into a single stream delta the same way
 * `<action-card>` is, and persisted verbatim inside `ChatEvent.data`.
 *
 * The JSON is LLM-produced, so every field is defensively coerced and clamped
 * here. Nothing from this payload ever reaches the DOM as markup — it only
 * flows into Chart.js `data`/`options` objects and Angular property bindings,
 * which is a different (safer) trust boundary than the markdown/innerHTML path.
 */

export type AlloChartType = 'bar' | 'donut' | 'line';

export interface AlloChartSeries {
  name: string;
  data: number[];
}

export interface AlloChartSpec {
  type: AlloChartType;
  title: string;
  unit: string;
  labels: string[];
  series: AlloChartSeries[];
}

/** Chart types the renderer knows how to draw; anything else falls back to 'bar'. */
const CHART_TYPES: readonly AlloChartType[] = ['bar', 'donut', 'line'];

/** Beyond this many categories a chart stops being readable in a chat bubble. */
const MAX_LABELS = 14;

/** Beyond this many series the palette repeats and the legend crowds the canvas. */
const MAX_SERIES = 4;

/** `unit` is a suffix (e.g. '%', 'bps'), never a sentence. */
const MAX_UNIT_LENGTH = 4;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toFiniteNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function coerceSeries(value: unknown): AlloChartSeries[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (entry): entry is Record<string, unknown> =>
        isRecord(entry) && Array.isArray(entry['data'])
    )
    .map(entry => ({
      name: typeof entry['name'] === 'string' ? entry['name'] : '',
      data: (entry['data'] as unknown[]).map(toFiniteNumber),
    }));
}

/**
 * Parses the raw JSON string from a `<chart>` block into a normalized spec.
 *
 * Returns `null` when the payload is unusable (unparseable, or missing labels
 * or series) so the caller can render a graceful text fallback rather than an
 * empty canvas.
 */
export function parseAlloChartSpec(raw: string): AlloChartSpec | null {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isRecord(parsed)) {
    return null;
  }

  const rawType = String(parsed['type']) as AlloChartType;
  const type = CHART_TYPES.includes(rawType) ? rawType : 'bar';

  let labels = Array.isArray(parsed['labels'])
    ? parsed['labels'].map(label => String(label))
    : [];
  let series = coerceSeries(parsed['series']);

  // Nothing to plot — the caller shows a fallback instead of an empty canvas.
  if (!labels.length || !series.length) {
    return null;
  }

  labels = labels.slice(0, MAX_LABELS);
  // Align every series to the (possibly clamped) label count; missing → 0.
  series = series.slice(0, MAX_SERIES).map(entry => ({
    name: entry.name,
    data: labels.map((_, index) => entry.data[index] ?? 0),
  }));

  return {
    type,
    title: typeof parsed['title'] === 'string' ? parsed['title'].trim() : '',
    unit:
      typeof parsed['unit'] === 'string'
        ? parsed['unit'].slice(0, MAX_UNIT_LENGTH)
        : '',
    labels,
    series,
  };
}

/**
 * Screen-reader summary for the bare `<canvas>`, carrying the actual data
 * values rather than just the chart type.
 */
export function buildChartAriaLabel(spec: AlloChartSpec): string {
  const kind = `${spec.type} chart${spec.title ? `: ${spec.title}` : ''}`;
  const multiSeries = spec.series.length > 1;

  const parts = spec.labels.map((label, index) => {
    const values = spec.series
      .map(entry => {
        const value = `${entry.data[index]}${spec.unit}`;
        return multiSeries && entry.name ? `${entry.name} ${value}` : value;
      })
      .join(', ');
    return `${label} ${values}`;
  });

  // No truncation here: `parseAlloChartSpec` already clamps labels to
  // MAX_LABELS, so every spec reaching this function is short enough to
  // summarize in full.
  const summary = parts.filter(Boolean).join('; ');

  return `${kind}.${summary ? ` ${summary}` : ''}`;
}
